
import { useFormik } from 'formik';
import { useEffect } from 'react';
import { useToastContext } from '@/common/components/ui/toast/toast.context';
import { BILLING_TYPE, PaymentInfo } from '@/types/payment.type';
import { IRenter } from '@/types/renter.type';
import { negotiationDebits } from '@/common/services/debits-negotiation.api';
import { usePaymentsData } from './usePaymentsData';
import { useDebitsCalculation } from './useDebitsCalculation';
import { calculateCreditCardInstallmentValue, getCreditCardFees } from '../helpers/payment-rules.helpers';
import { IDebitNegotiation } from '@/types/debits-negotiation.type';

interface UseDebitsNegotiationFormProps {
  renter: IRenter;
  payments: PaymentInfo[];
  enableManualEdit: boolean;
}

export const useDebitsNegotiationForm = ({
  renter,
  payments,
  enableManualEdit
}: UseDebitsNegotiationFormProps) => {
  const { notifyError, notifySuccess } = useToastContext();
  const { allPaymentsData } = usePaymentsData(payments);

  const initialValues = {
    paymentInfo: {
      externalPaymentsIds: [],
      ids: [],
      totalValue: 0,
      totalValueEdited: 0,
      description: "",
      amountCharged: 0,
      finalValue: 0,
    },
    allPayments: allPaymentsData,
    billingType: BILLING_TYPE.UNDEFINED,
    renterName: renter.name,
    renterId: renter._id!,

    creditCard: {
      hasEntry: false,
      entryPercentage: 0,
      entryValue: 0,
      installments: 1,
      cardValue: 0,
      finalCardValue: 0,
      installmentValue: 0,
      fees: {
        rate: 0,
        fixed: 0,
        description: ""
      },
      totalGeneral: 0
    },

    boletoOrPix: {
      entryPercentage: 0,
      entryValue: 0,
      installments: 1,
      remainingValue: 0,
      installmentValue: 0,
      hasInstallments: false,
      entryPercentageText: "",
      installmentsText: "",
      nextDueDate: ""
    }
  };

  const formik = useFormik({
    initialValues,
    onSubmit: async (values) => {
      try {
        const submitData: Partial<IDebitNegotiation> = {

          renterName: values.renterName,
          renterId: values.renterId,
          billingType: values.billingType,


          paymentInfo: values.paymentInfo,
          allPayments: values.allPayments,


          ...(values.billingType === 'CREDIT_CARD' && {
            creditCard: values.creditCard
          }),


          ...((['PIX', 'BOLETO'].includes(values.billingType)) && {
            boletoOrPix: values.boletoOrPix
          }),


          negotiationData: {
            enableManualEdit,
            originalTotalValue: values.paymentInfo.totalValue,
            editedTotalValue: values.paymentInfo.totalValueEdited,
            wasEdited: !!values.paymentInfo.totalValueEdited,
            timestamp: new Date().toISOString()
          }
        };

        const result = await negotiationDebits(submitData);

        if (!result.ok) {
          notifyError(result.message as string || "Erro ao salvar débitos");
          return;
        }

        notifySuccess("Débitos salvos com sucesso!");
      } catch (error) {
        console.error("Erro ao salvar débitos:", error);
        notifyError("Erro inesperado ao salvar débitos");
      }
    }
  });


  const { amountCharged, finalValue } = useDebitsCalculation({
    totalValue: formik.values.paymentInfo.totalValue,
    totalValueEdited: formik.values.paymentInfo.totalValueEdited,
    discountPercentage: formik.values.billingType === 'CREDIT_CARD'
      ? formik.values.creditCard.entryPercentage.toString()
      : formik.values.boletoOrPix.entryPercentage.toString(),
    enableManualEdit
  });


  useEffect(() => {
    formik.setFieldValue('paymentInfo.amountCharged', amountCharged);
    formik.setFieldValue('paymentInfo.finalValue', finalValue);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountCharged, finalValue]);


  useEffect(() => {
    if (formik.values.billingType === 'CREDIT_CARD') {
      const totalValue = formik.values.paymentInfo.totalValueEdited || formik.values.paymentInfo.totalValue;
      const entryPercentage = formik.values.creditCard.entryPercentage;
      const installments = formik.values.creditCard.installments;

      const entryValue = (entryPercentage / 100) * totalValue;
      const cardValue = entryPercentage > 0 ? totalValue - entryValue : totalValue;

      // ✅ APLICAR JUROS COMPOSTOS ACUMULATIVOS
      const fees = getCreditCardFees(installments);
      const monthlyValueWithFees = calculateCreditCardInstallmentValue(cardValue, installments);
      const finalCardValue = monthlyValueWithFees * installments;

      const totalGeneral = entryValue + finalCardValue;

      formik.setFieldValue('creditCard.entryValue', entryValue);
      formik.setFieldValue('creditCard.cardValue', cardValue);
      formik.setFieldValue('creditCard.finalCardValue', finalCardValue);
      formik.setFieldValue('creditCard.installmentValue', monthlyValueWithFees);
      formik.setFieldValue('creditCard.totalGeneral', totalGeneral);
      formik.setFieldValue('creditCard.fees', fees);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.billingType,
    formik.values.creditCard.entryPercentage,
    formik.values.creditCard.installments,
    formik.values.paymentInfo.totalValue,
    formik.values.paymentInfo.totalValueEdited
  ]);


  useEffect(() => {
    if (['PIX', 'BOLETO'].includes(formik.values.billingType)) {
      const totalValue = formik.values.paymentInfo.totalValueEdited || formik.values.paymentInfo.totalValue;
      const rawEntryPercentage = formik.values.boletoOrPix.entryPercentage;
      const installments = Number(formik.values.boletoOrPix.installments) || 1;

      const pct = Number(rawEntryPercentage) || 0;

      // se sem entrada (0) -> entryValue = 0 e permitir parcelamento sobre o total
      let entryValue = 0;
      if (pct > 0) {
        entryValue = Math.max((pct / 100) * totalValue, 50);
      }

      let remainingValue = totalValue - entryValue;
      if (remainingValue < 0) remainingValue = 0;

      // só considera parcelamento se remainingValue >= 50 e installments > 1
      const hasInstallments = installments > 1 && remainingValue >= 50;
      const installmentValue = hasInstallments ? remainingValue / installments : 0;

      const entryPercentageText = `${pct}% (entrada R$ ${entryValue.toFixed(2)})`;
      const installmentsText = hasInstallments
        ? `${installments}x de R$ ${installmentValue.toFixed(2)}`
        : "À vista";

      formik.setFieldValue('boletoOrPix.entryValue', entryValue);
      formik.setFieldValue('boletoOrPix.remainingValue', remainingValue);
      formik.setFieldValue('boletoOrPix.installmentValue', installmentValue);
      formik.setFieldValue('boletoOrPix.hasInstallments', hasInstallments);
      formik.setFieldValue('boletoOrPix.entryPercentageText', entryPercentageText);
      formik.setFieldValue('boletoOrPix.installmentsText', installmentsText);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.billingType,
    formik.values.boletoOrPix.entryPercentage,
    formik.values.boletoOrPix.installments,
    formik.values.paymentInfo.totalValue,
    formik.values.paymentInfo.totalValueEdited
  ]);

  return {
    formik,
    amountCharged,
    finalValue
  };
};